import React from 'react'
import { Card, CardContent, CardHeader, Label, Switch } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { listPlans } from '@/modules/plans/services/planService'
import type { Plan } from '@/modules/plans/types'
import {
  fetchPlanRewardsPolicies,
  fetchRoleRewardsPolicies,
  upsertPlanRewardsPolicy,
  upsertRoleRewardsPolicy,
} from '../../services/rewardsAccessService'

export default function RewardsSettingsAccessCard() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [rolePolicies, setRolePolicies] = React.useState<Record<string, boolean>>({})
  const [planPolicies, setPlanPolicies] = React.useState<Record<string, boolean>>({})
  const [plans, setPlans] = React.useState<Plan[]>([])

  React.useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        setLoading(true)
        const [roles, plansPolicies, plansResult] = await Promise.all([
          fetchRoleRewardsPolicies(),
          fetchPlanRewardsPolicies(),
          listPlans({ page: 1, pageSize: 200 }),
        ])
        if (!mounted) return
        const rolesMap: Record<string, boolean> = {}
        roles.forEach((r) => {
          rolesMap[r.role] = !!r.rewards_enabled
        })
        const plansMap: Record<string, boolean> = {}
        plansPolicies.forEach((p) => {
          plansMap[p.plan_id] = !!p.rewards_enabled
        })
        setRolePolicies(rolesMap)
        setPlanPolicies(plansMap)
        setPlans(plansResult.items)
      } catch (e: any) {
        toast({
          title: 'Rewards - Acesso',
          description: e?.message || 'Falha ao carregar configurações',
          variant: 'error',
        })
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [toast])

  async function toggleRole(role: string, next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await upsertRoleRewardsPolicy(role, next)
      setRolePolicies((prev) => ({ ...prev, [role]: next }))
      toast({
        title: 'Rewards - Acesso (Roles)',
        description: `Rewards para role "${role}" ${next ? 'ativado' : 'desativado'}.`,
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Rewards - Acesso (Roles)',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function togglePlan(planId: string, next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await upsertPlanRewardsPolicy(planId, next)
      setPlanPolicies((prev) => ({ ...prev, [planId]: next }))
      toast({
        title: 'Rewards - Acesso (Planos)',
        description: `Rewards para plano "${planId}" ${next ? 'ativado' : 'desativado'}.`,
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Rewards - Acesso (Planos)',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Rewards - Controle de acesso (role + plano)</CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">Acesso por papel (role)</div>
          <p className="text-xs text-foreground/60">
            Defina quais papéis podem visualizar o módulo Rewards. Para membros, o acesso efetivo também depende do plano.
          </p>
          <div className="mt-2 space-y-2">
            {['master', 'member', 'afiliado'].map((role) => (
              <div key={role} className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2">
                <div className="text-sm font-medium capitalize">{role}</div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Desativado</Label>
                  <Switch checked={!!rolePolicies[role]} disabled={loading || saving} onChange={(e) => toggleRole(role, (e.target as HTMLInputElement).checked)} />
                  <Label className="text-xs">Ativado</Label>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">Acesso por plano</div>
          <p className="text-xs text-foreground/60">
            Ative o módulo Rewards apenas para planos específicos. O acesso efetivo considera role + plano.
          </p>
          <div className="mt-2 space-y-2">
            {plans.map((plan) => (
              <div key={plan.id} className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2">
                <div className="text-sm font-medium">
                  {plan.name} <span className="text-xs text-foreground/60">({plan.id})</span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Desativado</Label>
                  <Switch checked={!!planPolicies[plan.id]} disabled={loading || saving} onChange={(e) => togglePlan(plan.id, (e.target as HTMLInputElement).checked)} />
                  <Label className="text-xs">Ativado</Label>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

